import { useEffect, useMemo, useState } from 'react'
import { View, Text, StyleSheet } from 'react-native'
import { useRouter, useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { colors, fonts } from '../lib/theme'
import { useThemeColors } from '../lib/useThemeColors'
import GradientButton from '../components/GradientButton'

export default function Join() {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const router = useRouter()
  const params = useLocalSearchParams()
  const { user, isLoggedIn, loading: authLoading } = useAuth()

  const leagueId = params.league
  const token = params.token   // nuovo: UUID opaco
  const password = params.pwd     // legacy: backward compat

  const [status, setStatus] = useState('loading') // loading | joining | success | error
  const [msg, setMsg] = useState('')

  useEffect(() => {
    if (authLoading) return

    // Non loggato → redirect al login con redirect back
    if (!isLoggedIn) {
      const target = `/join${leagueId ? `?league=${leagueId}` : ''}${token ? `${leagueId ? '&' : '?'}token=${token}` : ''}`
      router.replace(`/login?redirect=${encodeURIComponent(target)}`)
      return
    }

    if (!leagueId && !token) {
      router.replace('/')
      return
    }

    const join = async () => {
      setStatus('joining')
      try {
        let result

        if (token) {
          // Nuovo flusso: token opaco
          const { data, error } = await supabase.rpc('fn_join_league_by_token', {
            p_id_user: user.id,
            p_token: token,
          })
          if (error) throw error
          result = data?.[0]
        } else {
          // Legacy: password in chiaro
          const { data, error } = await supabase.rpc('fn_join_league', {
            p_id_user: user.id,
            p_id_league: parseInt(leagueId),
            p_password: password ?? null,
          })
          if (error) throw error
          result = data?.[0]
        }

        if (result?.success) {
          setStatus('success')
          setMsg(result.message ?? t('join.welcome'))
          setTimeout(() => router.replace('/Leagues'), 2000)
        } else {
          if (result?.message?.includes('già membro')) {
            setStatus('success')
            setMsg(t('join.already_member'))
            setTimeout(() => router.replace('/Leagues'), 1500)
          } else {
            setStatus('error')
            setMsg(result?.message ?? t('join.error'))
          }
        }
      } catch (e) {
        setStatus('error')
        setMsg(e.message)
      }
    }

    join()
  }, [authLoading, isLoggedIn, user, leagueId, token, password])

  const emoji = status === 'loading' || status === 'joining' ? '🔄'
    : status === 'success' ? '🎉' : '❌'

  const title = status === 'loading' || status === 'joining'
    ? t('join.joining')
    : status === 'success' ? t('join.success') : t('common.error_title')

  return (
    <View style={[styles.container, { backgroundColor: tc.bg }]}>
      <View style={[styles.card, { backgroundColor: tc.bgCard, borderColor: tc.border }]}>
        <Text style={styles.emoji}>{emoji}</Text>
        <Text style={[styles.title, { color: tc.text }]}>{title}</Text>
        {msg ? <Text style={[styles.msg, { color: tc.textMuted }]}>{msg}</Text> : null}
        {status === 'error' && (
          <GradientButton onPress={() => router.replace('/')} style={{ marginTop: 20 }}>
            {t('join.back_home')}
          </GradientButton>
        )}
      </View>
    </View>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  container: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 20 },
  card: { borderRadius: 20, borderWidth: 1, padding: 40, paddingHorizontal: 28, maxWidth: 360, width: '100%', alignItems: 'center' },
  emoji: { fontSize: 48, marginBottom: 16, textAlign: 'center' },
  title: { fontFamily: fonts.condensedBold, fontSize: 20, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, textAlign: 'center' },
  msg: { fontSize: 14, lineHeight: 22, textAlign: 'center' },
})